import * as Yup from 'yup';

import { isIreland } from 'src/lib/countryConfig';
import { customTermValidation } from 'src/validation/PricingValidation';

export interface CretePricingOfferInput {
    name: string;
    term: PricingTerm;
    customTerm?: number;
    type: PricingType;
    primaryProductIds?: number[];
    secondaryProductIds?: number[];
    salesConditions: SalesConditionsType;
    mccConditions?: SalesConditionsType;
    productPromo?: {
        productId: number;
        shouldApplyPromo: boolean;
    }[];
    gpvConditions?: SalesConditionsType;
    mccs?: { key: string; value: string }[];
    allPartners: string;
    gpvLowest: number;
    gpvHighest: number;
    pricing?: {
        productId: number;
        type: string;
        catalogue: ProductCatalogue;
        oneOff?: { lowest: number; highest: number };
        monthly?: { lowest: number; highest: number };
        options?: {
            oneOff: { sales: number; highest: number }[];
            monthly: { sales: number; highest: number }[];
        };
    };
}

export enum ProductCatalogue {
    PRIMARY = 'PRIMARY',
    SECONDARY = 'SECONDARY',
}

export enum PricingTerm {
    O12_MONTHS = 'O12_MONTHS',
    O18_MONTHS = 'O18_MONTHS',
    O24_MONTHS = 'O24_MONTHS',
    O36_MONTHS = 'O36_MONTHS',
    O48_MONTHS = 'O48_MONTHS',
    CUSTOM = 'CUSTOM',
}

export enum SalesConditionsType {
    NO = 'NO',
    YES = 'YES',
}

export interface DefaultPrices {
    productId: number | null;
    type: string | null;
    catalogue: string | null;
    disabled: string | null;
    oneOff: {
        lowest: number | null;
        highest: number | null;
    };
    monthly: {
        lowest: number | null;
        highest: number | null;
    };
    options: {
        oneOff: { sales: number | null; commission: number | null }[];
        monthly: { sales: number | null; commission: number | null }[];
    };
}

export interface MutltiplPrices {
    sales: number | null;
    commission: number | null;
}

export interface SelectedProducts {
    id: number;
    name: string;
    catalogue: string;
    imageUrl: string;
    type: PricingType;
    pricing: DefaultPrices;
    secondaryProductIds?: number[];
    message?: { [type: string]: string };
}

export enum PricingType {
    TERMINAL = 'TERMINAL',
    ONLINE = 'ONLINE',
    TRANSACTIONAL = 'TRANSACTIONAL',
}

export enum OfferStatus {
    ACTIVE = 'ACTIVE',
    DEACTIVATED = 'DEACTIVATED',
    DELETED = 'DELETED',
}

export interface TerminalOfferType {
    id: number;
    name: string;
    term: PricingTerm;
    customTerm?: number;
    type: PricingType;
    pricings: {
        catalogue: ProductCatalogue;
        type: string;
        prices: {
            monthly: { lowest: number; highest: number };
            oneOff: { lowest: number; highest: number };
            options: { sales: number; commission: number }[];
        };
        product: {
            id: number;
            name: string;
            imageUrl: string;
        };
    }[];
    primaryProducts: {
        id: number;
        name: string;
    }[];
    secondaryProducts: {
        id: number;
        name: string;
    }[];
    salesConditions: SalesConditionsType;
}

export interface UpdateTerminalInput {
    id: number;
    name: string;
    term: PricingTerm;
    partnerIds: number[];
    customTerm?: number;
    type: PricingType;
    status: OfferStatus;
    primaryProductIds?: number[];
    secondaryProductIds?: number[];
    salesConditions: string;
    mccConditions?: string;
    mccs?: { key: string; value: string }[];
    gpvConditions?: string;
    allPartners: string;
    gpvLowest: number;
    gpvHighest: number;
    pricing?: {
        productId: number;
        type: string;
        catalogue: ProductCatalogue;
        oneOff?: { lowest: number; highest: number };
        monthly?: { lowest: number; highest: number };
        options?: {
            oneOff: { sales: number; highest: number }[];
            monthly: { sales: number; highest: number }[];
        };
    };
}

export interface FilterInputs {
    id?: string;
    name?: string;
    status?: string;
    type: PricingType;
}
const GBContractTerms = [
    { key: 'O12_MONTHS', value: '12 Months' },
    { key: 'O18_MONTHS', value: '18 Months' },
    { key: 'CUSTOM', value: 'Custom Term' },
];
const IEContractTerms = [
    { key: 'O12_MONTHS', value: '12 Months' },
    { key: 'O24_MONTHS', value: '24 Months' },
    { key: 'O36_MONTHS', value: '36 Months' },
    { key: 'O48_MONTHS', value: '48 Months' },
    { key: 'CUSTOM', value: 'Custom Term' },
];
export const contractTermsMenuItems = isIreland ? IEContractTerms : GBContractTerms;

export const updatePricingAlertMessage = `Please note, the changes you have made to this offer shall not affect any previous merchants/applications
  that are live or inflight. The system will duplicate the offer with the new values and
  create a new Offer and assign it a new offer ID and will auto assign it to all partners that had it before.
  The existing offer values will not be changed and will be assigned a historical offer ID for audit purposes.
  The historical offer ID will be ‘Legacy - OFFERID' and can still be found in the Terminal offer list.`;

export const deactivatePricingAlertMessage = `This product is live in a number of offers. If this product is deactivated,
  it will also deactivate the offers listed below and prevent them being used for new boarding.
  To avoid impact, remove or replace the product in the associated offers`;

export const pricingTypes = {
    MONTHLY: 'Monthly',
    ONE_OFF: 'One-off',
    BOTH: 'Both',
};

export const PricingLimit = {
    oneOff: {
        lowest: 'one-off-lowest',
        highest: 'one-off-highest',
    },
    monthly: {
        lowest: 'monthly-lowest',
        highest: 'monthly-highest',
    },
};

export const terminalPricingOfferValidationSchema = Yup.object().shape({
    customTerm: customTermValidation,
});
